/*
 Designed and developed by Richard Nesnass

 This file is part of SL+.

 SL+ is free software: you can redistribute it and/or modify
 it under the terms of the GNU Affero General Public License as published by
 the Free Software Foundation, either version 3 of the License, or
 (at your option) any later version.

 GPL-3.0-only or GPL-3.0-or-later

 SL+ is distributed in the hope that it will be useful,
 but WITHOUT ANY WARRANTY; without even the implied warranty of
 MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 GNU Affero General Public License for more details.

 You should have received a copy of the GNU Affero General Public License
 along with SL+.  If not, see <http://www.gnu.org/licenses/>.
 */
import { isRef, ref } from 'vue'
import type { Ref } from 'vue'

/**
 * Wrap a variable of specified type in a Vue Ref
 * @param element to wrap
 * @returns wrapped element
 */
const wrap = <T>(element: Ref<T> | T): Ref<T> => {
  if (isRef(element)) {
    return element
  }
  return ref(element) as Ref<T>
}

// https://stackoverflow.com/questions/3552461/how-to-format-a-javascript-date
/**
 * Format a date object into a string including day, month name, year and time
 * @param date
 * @returns string
 */
const dateToFormattedString = (date?: Date): string => {
  if (!date) return 'X'
  const monthNames = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ]

  const day = date.getDate()
  const monthIndex = date.getMonth()
  const year = date.getFullYear()

  let hours = date.getHours().toString()
  let mins = date.getMinutes().toString()

  hours = hours.length == 1 ? '0' + hours : hours
  mins = mins.length == 1 ? '0' + mins : mins

  return day + ' ' + monthNames[monthIndex] + ' ' + year + ' | ' + hours + ':' + mins
}

/// <summary>
/// Creates a new filter that processes dates and also delegates to a chain filter optionaly.
/// </summary>
/// <param name="chainFilter" type="Function">property name that is parsed</param>
/// <returns type="Function">returns a new chainning filter for dates</returns>
const createDateParser = function () {
  const reISO =
    /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2}(?:\.{0,1}\d*))(?:Z|(\+|-)([\d|:]*))?$/
  //const reMsAjax = /^\/Date\((d|-|.*)\)[\/|\\]$/
  return function (key: string, value: unknown) {
    let parsedValue = value
    if (typeof value === 'string') {
      const a = reISO.exec(value)
      if (a) {
        parsedValue = new Date(value)
      }
    }
    return parsedValue
  }
}

export function parseWithDate<T>(json: string) {
  // JSON.parseWithDate =
  /// <summary>
  /// Wrapper around the JSON.parse() function that adds a date
  /// filtering extension. Returns all dates as real JavaScript dates.
  /// </summary>
  /// <param name="json" type="string">JSON to be parsed</param>
  /// <returns type="any">parsed value or object</returns>
  const parse = JSON.parse
  try {
    //const data = JSON.stringify(json)
    const res = parse(json, createDateParser()) as T
    return res
  } catch (e) {
    // orignal error thrown has no error message so rethrow with message
    throw new Error('JSON content could not be parsed' + e)
  }
}

function capitalizeFirstLetter(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1)
}

// Filter undefined values from an array of T
function filterUndef<T>(ts: (T | undefined)[]): T[] {
  return ts.filter((t: T | undefined): t is T => !!t)
}

// Random UUID. See https://gist.github.com/jed/982883
/**
 * Generate a ranom ID
 */
const uuid = (a = ''): string =>
  a
    ? ((Number(a) ^ (Math.random() * 16)) >> (Number(a) / 4)).toString(16)
    : `${1e7}-${1e3}-${4e3}-${8e3}-${1e11}`.replace(/[018]/g, uuid)

/**
 * Dispatch a regular error as an 'kmerror'
 * @param error
 */
const emitError = (error: Error): void => {
  const e = new CustomEvent<Error>('kmerror', {
    detail: error,
  })
  window.dispatchEvent(e)
}

/**
 * Async: Wait a number of milliseconds before resolving
 * @param ms length of time to wait (milliseconds)
 * @returns void
 */
const wait = (ms: number): Promise<void> => {
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve()
    }, ms)
  })
}

export const secondsToTime = function (seconds: number): string {
  const d = new Date(Date.UTC(1970, 0, 1))
  d.setUTCSeconds(seconds)
  const h = d.getUTCHours() < 10 ? `0${d.getUTCHours()}` : d.getUTCHours()
  const m = d.getUTCMinutes() < 10 ? `0${d.getUTCMinutes()}` : d.getUTCMinutes()
  const s = d.getUTCSeconds() < 10 ? `0${d.getUTCSeconds()}` : d.getUTCSeconds()
  return `${d.getUTCHours() > 0 ? h + ':' : ''}${m}:${s}`
}

// Input time as a number - seconds as whole with milliseconds as the decimal e.g. 12.65 = 12 seconds 650 milliseconds
const formatTime = (timeInSeconds: number, offsetTime = 0): string => {
  // Adjust for offset
  const adjustedTimeInSeconds = Math.floor(timeInSeconds - offsetTime)

  let minutes = Math.round(adjustedTimeInSeconds / 60)
  const hours = Math.round(minutes / 60)
  // prettier-ignore
  minutes = hours > 0 ? Math.round(minutes % (60 * hours)) : minutes
  const seconds =
    minutes > 0
      ? Math.round(adjustedTimeInSeconds % (60 * minutes + 60 * 60 * hours))
      : Math.round(adjustedTimeInSeconds)
  const minutesString = minutes > 9 ? minutes : '0' + minutes
  const secondsString = seconds > 9 ? seconds : '0' + seconds
  return `${hours > 0 ? hours + ':' : ''}${minutesString}:${secondsString}`
}

export {
  uuid,
  filterUndef,
  dateToFormattedString,
  capitalizeFirstLetter,
  wrap,
  wait,
  emitError,
  formatTime,
}
